<script setup>
import {ref, inject, computed, watch} from "vue";
import needsStartupsService from "@/services/needs-startups.service";
import {getLogo} from "@/helpers/logo";

const emit = defineEmits(['updated'])

const needId = inject('needId');
const showModal = ref(false);
const showDetails = ref(false);
const startups = ref([]);
const index = ref(0);
const startupDetails = ref(null);
const updated = ref(false);

const startup = computed(() => startups.value[index.value]);

watch(showDetails, (value) => {
    if (value) {
        getStartupInfo();
    }
});

watch(startup, () => {
    showDetails.value = false;
});

watch(showModal, (value) => {
    if (!value) {
        emit('updated');
    }
});

async function getStartups() {
    const {data} = await needsStartupsService.getUnreviewedStartups(needId);
    startups.value = data;
}

function skip() {
    if (index.value === startups.value.length - 1) {
        index.value = 0;
    } else {
        ++index.value;
    }
}

async function rate(result) {
    await needsStartupsService.setStartupMatchStatus(needId, startup.value.id, result);
    updated.value = true;

    startups.value.splice(index.value, 1);

    if (!startups.value.length) {
        showModal.value = false;
    } else if (index.value >= startups.value.length) {
        index.value = 0;
    }
}

async function getStartupInfo() {
    startupDetails.value = null;

    const {data} = await needsStartupsService.getStartupDetails(needId, startup.value.id);
    startupDetails.value = data;
}

function open() {
    showModal.value = true;
    showDetails.value = false;
    getStartups();
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer modal-class="modal-matchmaking">
        <template v-if="startups.length">
            <div
                class="modal-matchmaking__icon-wrapper"
                @click="rate(2)"
            >
                <div class="modal-matchmaking__close"></div>
            </div>

            <div class="modal-matchmaking__wrapper">
                <div class="modal-matchmaking__content"></div>
                <div class="modal-matchmaking__content"></div>
                <div class="modal-matchmaking__content" :class="{ 'modal-matchmaking__content--expanded': showDetails }">
                    <header class="modal-matchmaking__content__header">
                        <div class="image-block">
                            <img :src="getLogo(startup.logo)" alt=""/>
                        </div>
                        <div>
                            <h5 class="heading5 heading5--secondary">{{startup.name}}</h5>
                            <!-- <p class="modal-matchmaking__content__by modal-matchmaking__content__by--recommended">Recommended by AI</p> -->
                            <p v-if="false" class="modal-matchmaking__content__by modal-matchmaking__content__by--sent">Sent by NEA</p>
                        </div>

                        <div class="vc-label">AI Recommended</div>

                        <div v-if="showDetails" @click="showDetails = false" class="modal-matchmaking__minimize"></div>
                    </header>

                    <template v-if="!showDetails">
                        <div class="modal-matchmaking__content__inner">
                            <div class="modal-matchmaking__stat">
                                <div class="modal-matchmaking__stat__item modal-matchmaking__stat__item--match">
                                    njknkkjbkbk
                                </div>
                                <div class="modal-matchmaking__stat__item modal-matchmaking__stat__item--score">
                                    njknkkjbkbk
                                </div>
                            </div>
                            <!-- <p>Vel pharetra vel turpis nunc eget lorem dolor sed viverra.</p> -->
                            <!-- <p>Nec sagittis aliquam malesuada bibendum arcu vitae elementum curabitur vitae.</p> -->
                            <!-- <h5 class="heading5 heading5--secondary">Clients:</h5> -->
                            <!-- <ul class="list">
                                <li>Microsoft</li>
                                <li>OpenAI</li>
                                <li>Oracle</li>
                            </ul> -->

                            <p>{{startup.description}}</p>
                            <div class="modal-matchmaking__textarea">
                                <p>Malcolm Reynolds, <b>NEA</b>:</p>
                                <p><i>Gamma Core is an exciting startup that has been scaling up quickly and would be a good fit for your need. They spent the last year working with Lorem Corp successfully and are ready for their next challenge.</i></p>
                            </div>
                        </div>
                        <footer class="modal-matchmaking__content__footer">
                            <a href="#" @click.prevent="showDetails = true">More Details</a>
                        </footer>
                    </template>

                    <template v-else-if="startupDetails">
                        <div class="modal-matchmaking__content__inner">
                            <div class="modal-matchmaking__content__details">
                                <ul class="matchmaking__overview">
                                    <li class="matchmaking__overview__name"><a href="#">{{ startupDetails.website }}</a></li>
                                    <li class="matchmaking__overview__funding">$40M Raised</li>
                                    <li class="matchmaking__overview__income">25M Annual Income</li>
                                    <li class="matchmaking__overview__employees">51-100 Employees</li>
                                    <li class="matchmaking__overview__location">Toronto, Canada</li>
                                    <li class="matchmaking__overview__startups">Series A</li>
                                    <li class="matchmaking__overview__founded">Founded 2015</li>
                                </ul>

                                <div class="matchmaking__about">
                                    <h5 class="heading5 heading5--secondary mb-4">About Gamma Core:</h5>

                                    <p>Automation Hero's award-winning intelligent document processing platform lets you get up and running within minutes, provides value within hours, and is future-proof for years to come. Connect with any IT infrastructure, click together no-code workflows with the industry's most accurate AI, seamlessly integrate your workforce into the process, and get real-time insights.</p>

                                    <div class="modal-matchmaking__stat">
                                        <div class="modal-matchmaking__stat__item modal-matchmaking__stat__item--match">
                                            njknkkjbkbk
                                        </div>
                                        <div class="modal-matchmaking__stat__item modal-matchmaking__stat__item--score">
                                            njknkkjbkbk
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="card-headline">
                                <header class="card-headline__header">Features</header>

                                <div class="card-headline__content">
                                    <p><b>User Engagement:</b> Toonimo offers interactive walkthroughs, guided tours, and custom animations to engage users and assist them in navigating websites.</p>

                                    <p><b>Multilingual Support:</b> Toonimo supports multiple languages, ensuring that users from diverse backgrounds receive the right information in their preferred language.</p>

                                    <p><b>Analytics:</b> Toonimo has a comprehensive analytics dashboard for tracking user engagement and conversions, allowing businesses to monitor performance and adjust strategies accordingly.</p>

                                    <p><b>Integrations:</b> Toonimo integrates with various platforms and systems like Salesforce, enabling seamless collaboration between departments and enhancing workflow efficiency.</p>

                                    <p><b>Accessibility:</b> Toonimo caters to audiences with special needs, providing features like screen reader compatibility and keyboard navigation to ensure equal opportunities for all users.</p>

                                    <p><b>Custom Animations:</b> Toonimo uses custom animations and interactions to create a dynamic, visually appealing interface that captures users' attention and promotes exploration.</p>

                                    <p><b>Survey Capabilities:</b> Toonimo includes survey functionality, enabling businesses to gather valuable feedback from users and make informed decisions about product development and improvement.</p>
                                </div>
                            </div>

                            <div class="card-headline">
                                <header class="card-headline__header">News</header>

                                <div class="card-headline__content">
                                    <div class="card card--bordered">
                                        <div>
                                            <a href="#" class="card-headline__url">worldnews.com</a>

                                            <h6 class="heading6"><a href="#">NobleAI Secures China Patent</a></h6>

                                            <time class="card-headline__time">June 6, 2024</time>
                                            <p>Vel pharetra vel turpis nunc eget lorem dolor sed viverra. Aliquet enim tortor at auctor urna nunc id. Eget aliquet nibh praesent tristique magna. Nulla facilisi…</p>
                                        </div>
                                        <figure class="card-headline__img">
                                            <img src="/img/global.e72ae145.png" alt="">
                                        </figure>
                                    </div>
                                    <div class="card card--bordered">
                                        <div>
                                            <a href="#" class="card-headline__url">worldnews.com</a>

                                            <h6 class="heading6"><a href="#">NobleAI Secures China Patent</a></h6>

                                            <time class="card-headline__time">June 6, 2024</time>
                                            <p>Vel pharetra vel turpis nunc eget lorem dolor sed viverra. Aliquet enim tortor at auctor urna nunc id. Eget aliquet nibh praesent tristique magna. Nulla facilisi…</p>
                                        </div>
                                        <figure class="card-headline__img">
                                            <img src="/img/global.e72ae145.png" alt="">
                                        </figure>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="modal-matchmaking__bottom">
                    <a
                        class="modal-matchmaking__skip"
                        @click="skip"
                    >
                        Skip
                    </a>
                </div>
            </div>

            <div
                class="modal-matchmaking__icon-wrapper"
                @click="rate(1)"
            >
                <div class="modal-matchmaking__love"></div>
            </div>
        </template>
    </b-modal>
</template>

<style scoped>

</style>
